import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, CalendarIcon, ClockIcon, FilterIcon, SearchIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// Mock data for collaboration opportunities
const collaborationOpportunities = [{
  id: '1',
  title: 'Co-Author for Fantasy Series',
  description: 'Looking for a co-author to collaborate on a 3-book fantasy series with established world-building and character arcs.',
  deadline: '2023-12-15',
  postedDate: '2023-10-01',
  authorName: 'Marcus Chen',
  authorId: '2',
  authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  genres: ['Fantasy', 'Young Adult'],
  projectType: 'Co-authoring',
  projectLength: 'Long-term (6+ months)',
  compensation: 'Royalty Split',
  requirements: 'Previous experience writing fantasy novels preferred. Must be able to match established tone and style.'
}, {
  id: '2',
  title: 'Short Story Collection - Horror Theme',
  description: 'Curating a collection of horror short stories from multiple authors. Looking for original, bone-chilling tales between 3,000-5,000 words.',
  deadline: '2023-11-30',
  postedDate: '2023-09-15',
  authorName: 'Sarah Johnson',
  authorId: '3',
  authorAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  genres: ['Horror', 'Supernatural'],
  projectType: 'Anthology',
  projectLength: 'Short-term (< 1 month)',
  compensation: 'Fixed Fee per Story',
  requirements: 'Must be comfortable with dark themes and psychological horror. No excessive gore or violence.'
}, {
  id: '3',
  title: 'Ghostwriter for Memoir',
  description: 'Seeking a skilled ghostwriter to help craft a personal memoir about overcoming adversity and finding success in the business world.',
  deadline: '2023-12-30',
  postedDate: '2023-10-10',
  authorName: 'David Williams',
  authorId: '4',
  authorAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  genres: ['Non-Fiction', 'Memoir', 'Business'],
  projectType: 'Ghostwriting',
  projectLength: 'Medium-term (3-6 months)',
  compensation: 'Fixed Fee (Negotiable)',
  requirements: 'Experience with memoir writing and business narratives. Strong interviewing skills required.'
}, {
  id: '4',
  title: 'Science Fiction Novel Collaboration',
  description: 'Collaborative opportunity for a near-future sci-fi novel exploring themes of AI ethics and human-machine relationships.',
  deadline: '2024-02-28',
  postedDate: '2023-10-05',
  authorName: 'Amara Knight',
  authorId: '5',
  authorAvatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  genres: ['Science Fiction', 'Speculative Fiction'],
  projectType: 'Co-authoring',
  projectLength: 'Medium-term (3-6 months)',
  compensation: 'Royalty Split + Advance',
  requirements: 'Must have published at least one sci-fi novel. Knowledge of AI and technology trends a plus.'
}, {
  id: '5',
  title: 'Mystery Series Continuation',
  description: 'Looking for an author to continue a popular mystery series (5 books published) with established characters and setting.',
  deadline: '2023-11-15',
  postedDate: '2023-09-20',
  authorName: 'Elena Richards',
  authorId: '1',
  authorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  genres: ['Mystery', 'Thriller'],
  projectType: 'Series Continuation',
  projectLength: 'Long-term (6+ months)',
  compensation: 'Royalty Split + Fixed Fee',
  requirements: 'Must be able to match existing writing style and tone. Experience with mystery/thriller genres required.'
}];
type ProjectLength = 'Any' | 'Short-term (< 1 month)' | 'Medium-term (3-6 months)' | 'Long-term (6+ months)';
type ProjectType = 'Any' | 'Co-authoring' | 'Anthology' | 'Ghostwriting' | 'Series Continuation';
type CompensationType = 'Any' | 'Royalty Split' | 'Fixed Fee' | 'Royalty Split + Advance' | 'Royalty Split + Fixed Fee';
const AuthorCollaborations = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [projectLength, setProjectLength] = useState<ProjectLength>('Any');
  const [projectType, setProjectType] = useState<ProjectType>('Any');
  const [compensationType, setCompensationType] = useState<CompensationType>('Any');
  const [showFilters, setShowFilters] = useState(false);
  const allGenres = Array.from(new Set(collaborationOpportunities.flatMap(opp => opp.genres)));
  const filteredOpportunities = collaborationOpportunities.filter(opp => {
    const matchesSearch = searchTerm === '' || opp.title.toLowerCase().includes(searchTerm.toLowerCase()) || opp.description.toLowerCase().includes(searchTerm.toLowerCase()) || opp.authorName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenres = selectedGenres.length === 0 || selectedGenres.some(genre => opp.genres.includes(genre));
    const matchesProjectLength = projectLength === 'Any' || opp.projectLength === projectLength;
    const matchesProjectType = projectType === 'Any' || opp.projectType === projectType;
    const matchesCompensation = compensationType === 'Any' || opp.compensation === compensationType;
    return matchesSearch && matchesGenres && matchesProjectLength && matchesProjectType && matchesCompensation;
  });
  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  };
  // Function to format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Calculate days remaining until deadline
  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-17484" data-mp="10gl6jbs49wsl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-17485" data-mp="15csetb7zjkdp">
        <div className="mb-8" data-id="element-17486" data-mp="2pvmdyezkloqs">
          <div className="mb-4" data-id="element-17487" data-mp="2pvvuzd2n0lkc">
            <BackButton data-id="element-17488" data-mp="2puuir59vxt11" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-17489" data-mp="2kwt0gmka9xqm">
            Author Collaboration Opportunities
          </h1>
          <p className={`mt-2 ${dk.textSecondary}`} data-id="element-17490" data-mp="2g4rgeegvj2q1">
            Find exciting collaboration opportunities with authors looking for
            co-writers, ghostwriters, and more
          </p>
        </div>

        {/* Search and Filters */}
        <div className={`rounded-lg shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-17491" data-mp="2731q5byqa9tu">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-17492" data-mp="224gor97qpk3t">
            <div className="relative flex-1" data-id="element-17493" data-mp="1x7gstr4ltxqf">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-17494" data-mp="1s83vgcg3x89l" />
              <input type="text" placeholder="Search opportunities by title, description, or author..." className={`pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-17495" data-mp="3rjwt8bapyhnd" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center font-medium text-sm ${rt.accentLink}`} data-id="element-17496" data-mp="2mnfr7fhf54kq">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-17497" data-mp="2cprtcgoingyc" />
              Filters {showFilters ? '(Hide)' : '(Show)'}
            </button>
          </div>

          {showFilters && <div className={`border-t pt-4 space-y-4 ${dk.border}`} data-id="element-17498" data-mp="23pmly6jboik6">
              <div data-id="element-17499" data-mp="23rfvpup8fiwe">
                <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-17500" data-mp="27qyh0rkytvzr">
                  Filter by Genre
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-17501" data-mp="1z1fr1690oim">
                  {allGenres.map(genre => <button key={genre} onClick={() => toggleGenre(genre)} className={`px-3 py-1 rounded-full text-sm border transition-all ${selectedGenres.includes(genre) ? rt.badge : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-17502" data-mp="5yk1fz5w93mi">
                      {genre}
                    </button>)}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-17503" data-mp="1vfe6xt9wa7h3">
                {[{
              label: 'Project Length',
              value: projectLength,
              onChange: (v: string) => setProjectLength(v as ProjectLength),
              options: [['Any', 'Any Length'], ['Short-term (< 1 month)', 'Short-term'], ['Medium-term (3-6 months)', 'Medium-term (3-6 months)'], ['Long-term (6+ months)', 'Long-term (6+ months)']]
            }, {
              label: 'Project Type',
              value: projectType,
              onChange: (v: string) => setProjectType(v as ProjectType),
              options: [['Any', 'Any Type'], ['Co-authoring', 'Co-authoring'], ['Anthology', 'Anthology'], ['Ghostwriting', 'Ghostwriting'], ['Series Continuation', 'Series Continuation']]
            }, {
              label: 'Compensation',
              value: compensationType,
              onChange: (v: string) => setCompensationType(v as CompensationType),
              options: [['Any', 'Any Compensation'], ['Royalty Split', 'Royalty Split'], ['Fixed Fee', 'Fixed Fee'], ['Royalty Split + Advance', 'Royalty Split + Advance'], ['Royalty Split + Fixed Fee', 'Royalty Split + Fixed Fee']]
            }].map(({
              label,
              value,
              onChange,
              options
            }) => <div key={label} data-id="element-17504" data-mp="3kvlj03amq5jv">
                    <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-17505" data-mp="3kwmvmc7an03p">
                      {label}
                    </label>
                    <select value={value} onChange={e => onChange(e.target.value)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-17506" data-mp="3uszml2bgezur">
                      {options.map(([val, lbl]) => <option key={val} value={val} data-id="element-17507" data-mp="cdp34dt99olf">
                          {lbl}
                        </option>)}
                    </select>
                  </div>)}
              </div>
            </div>}
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-17508" data-mp="29ebh9d11l4lu">
          {filteredOpportunities.map(opportunity => {
          const daysRemaining = getDaysRemaining(opportunity.deadline);
          return <div key={opportunity.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-17509" data-mp="2ifrnd8ktqkk9">
                <div className="p-6" data-id="element-17510" data-mp="2s7p22pb1junq">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-17511" data-mp="2s6mfgtkr4edm">
                    <div data-id="element-17512" data-mp="364kp5lo4nmms">
                      <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-17513" data-mp="317tyk4ep58n4">
                        {opportunity.title}
                      </h2>
                      <div className="flex items-center mt-2 gap-2" data-id="element-17514" data-mp="1bt5xv0fgxf4x">
                        <Link to={`/author/${opportunity.authorId}`} className="flex items-center group" data-id="element-17515" data-mp="16ucljbbx3u6c">
                          <img src={opportunity.authorAvatar} alt={opportunity.authorName} className="h-8 w-8 rounded-full object-cover mr-2" data-id="element-17516" data-mp="3ngix597354pr" />
                          <span className={`text-sm ${dk.textSecondary} group-hover:${rt.textPrimary}`} data-id="element-17517" data-mp="3dod8o9g1l5fb">
                            {opportunity.authorName}
                          </span>
                        </Link>
                        <span className={dk.textFaint} data-id="element-17518" data-mp="2qr1u71zhlwrr">•</span>
                        <span className={`text-sm flex items-center ${dk.textMuted}`} data-id="element-17519" data-mp="2ur25wev7btv4">
                          <CalendarIcon className="h-4 w-4 mr-1" data-id="element-17520" data-mp="ozoquhd63wxt" />
                          Posted {formatDate(opportunity.postedDate)}
                        </span>
                      </div>
                    </div>
                    <div className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${daysRemaining <= 7 ? 'bg-red-100 text-red-800' : daysRemaining <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-17521" data-mp="48jg7rg57d9k">
                      <ClockIcon className="h-4 w-4 mr-1" data-id="element-17522" data-mp="3lh6hgj8owgbv" />
                      {daysRemaining} days left
                    </div>
                  </div>

                  <p className={`mb-6 ${dk.textSecondary}`} data-id="element-17523" data-mp="3col3rhjx0ztw">
                    {opportunity.description}
                  </p>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-17524" data-mp="37qt76a8bspjd">
                    {[{
                  label: 'PROJECT TYPE',
                  value: opportunity.projectType,
                  icon: <BookIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-17525" data-mp="2tpqo86cmnvco" />
                }, {
                  label: 'LENGTH',
                  value: opportunity.projectLength,
                  icon: <CalendarIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-17526" data-mp="1i9pbarfdbcik" />
                }, {
                  label: 'COMPENSATION',
                  value: opportunity.compensation,
                  icon: null
                }, {
                  label: 'DEADLINE',
                  value: formatDate(opportunity.deadline),
                  icon: null
                }].map(({
                  label,
                  value,
                  icon
                }) => <div key={label} data-id="element-17527" data-mp="bsu3ry0yoekk">
                        <h3 className={`text-xs font-medium mb-1 ${dk.textFaint}`} data-id="element-17528" data-mp="brsg8u2rdzly">
                          {label}
                        </h3>
                        <p className={`text-sm flex items-center ${dk.textPrimary}`} data-id="element-17529" data-mp="3vuvquk6jo8ha">
                          {icon}
                          {value}
                        </p>
                      </div>)}
                  </div>

                  <div className="mb-6" data-id="element-17530" data-mp="3gzpm00j6qfpj">
                    <h3 className={`text-xs font-medium mb-2 ${dk.textFaint}`} data-id="element-17531" data-mp="3h0hg16x4w5dp">
                      GENRES
                    </h3>
                    <div className="flex flex-wrap gap-1.5" data-id="element-17532" data-mp="3qxdgqqgsjn7p">
                      {opportunity.genres.map(genre => <span key={genre} className={`text-xs px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-17533" data-mp="3lwzgvvpcfjkr">
                          {genre}
                        </span>)}
                    </div>
                  </div>

                  <div className={`border-t pt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${dk.border}`} data-id="element-17534" data-mp="2j3tyqzz2nr3m">
                    <div data-id="element-17535" data-mp="2a5i7cvqdspa4">
                      <h3 className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-17536" data-mp="2a6b7veyqyijt">
                        Requirements
                      </h3>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-17537" data-mp="32sm7onej4ffk">
                        {opportunity.requirements}
                      </p>
                    </div>
                    <button className={`px-6 py-2 rounded-md text-sm font-medium whitespace-nowrap text-white ${rt.primaryButton}`} data-id="element-17538" data-mp="36wk7z1flbwa7">
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>;
        })}

          {filteredOpportunities.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-17539" data-mp="3qk1easdc9iwm">
              <BookIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-17540" data-mp="1lpqvjssg27se" />
              <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-17541" data-mp="1lnnvefxkuy9y">
                No opportunities found
              </h3>
              <p className={dk.textMuted} data-id="element-17542" data-mp="1brunj9uszsyi">
                Try adjusting your search or filters
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default AuthorCollaborations;